import { User } from "./User";

// Simplified payment model from backend DTO (excludes internal Stripe details)
export interface Payment {
  id: string;
  type: string;
  provider?: string;
  amount: number;
  currency: string;
  status: string;
  description?: string;
  reference?: string;
  checkout_url?: string;
  payable_entity_id?: number;
  payable_id?: number;
  fee_amount?: number;
  net_amount?: number;
  paid_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionEvent {
  id: string;
  subscription_id: string;
  event_type: string;
  source: string;
  summary?: string | null;
  prior_status?: string | null;
  new_status?: string | null;
  occurred_at: string;
  created_at: string;
}

// Simplified subscription model from backend DTO (excludes internal Stripe details)
export interface Subscription {
  id: string;
  type: string;
  provider?: string;
  name: string;
  description?: string;
  amount: number;
  currency: string;
  billing_interval: string;
  interval_count: number;
  current_period_starts_at?: string;
  current_period_ends_at?: string;
  trial_period_days?: number;
  trial_ends_at?: string;
  status: string;
  auto_cancel_at?: string;
  cancelled_at?: string;
  reference?: string;
  checkout_url?: string;
  last_synced_at?: string;
  created_at: string;
  updated_at: string;
  customer_email?: string;
  customer_name?: string;
}

export interface PaymentLineItem {
  name: string;
  description?: string;
  unit_amount: number;
  quantity: number;
  image_url?: string;
  metadata?: Record<string, string>;
}

export interface CreatePaymentRequest {
  type: string;
  amount: number;
  currency: string;
  description: string;
  reference?: string;
  customer_email?: string;
  line_items?: PaymentLineItem[];
  success_url?: string;
  cancel_url?: string;
  metadata?: Record<string, string>;
}

export interface CreateSubscriptionRequest {
  name: string;
  description: string;
  type: string;
  amount: number;
  currency: string;
  billing_interval: string;
  interval_count: number;
  trial_period_days?: number;
  product_name?: string;
  product_description?: string;
  reference?: string;
  subscriber_entity_id?: number;
  subscriber_id?: number;
  customer_email: string;
  customer_name?: string;
  customer_phone?: string;
  customer_metadata?: Record<string, string>;
  success_url?: string;
  cancel_url?: string;
  metadata?: Record<string, string>;
}

// Simplified response types (excludes internal Stripe object)
export interface CreatePaymentResponse {
  anythink_pay_id: string;
  checkout_url?: string;
  session_id?: string;
}

export interface CreateSubscriptionResponse {
  anythink_pay_id: string;
  checkout_url?: string;
  session_id?: string;
}

export interface SubscriptionCheckAccessItem {
  id: string;
  name: string;
  status: string;
}

export interface SubscriptionCheckAccessResponse {
  has_access: boolean;
  subscription?: SubscriptionCheckAccessItem;
}

export interface SubscriptionPlan {
  id: number;
  plan_name: string;
  name: string;
  description: string;
  type: string;
  amount: number;
  currency: string;
  billing_interval: string;
  interval_count: number;
  trial_period_days?: number;
  product_name?: string;
  product_description?: string;
  reference?: string;
  is_active: boolean;
  stripe_product_id?: string;
  stripe_price_id?: string;
  apple_product_id?: string;
  apple_subscription_group_id?: string;
  tier_rank?: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateSubscriptionPlanRequest {
  plan_name: string;
  name: string;
  description: string;
  type: string;
  amount: number;
  currency: string;
  billing_interval: string;
  interval_count: number;
  trial_period_days?: number;
  product_name?: string;
  product_description?: string;
  reference?: string;
  is_active?: boolean;
  apple_product_id?: string;
  apple_subscription_group_id?: string;
  tier_rank?: number | null;
}

export interface UpdateSubscriptionPlanRequest extends CreateSubscriptionPlanRequest {}

export interface AppleIapCredentials {
  bundle_id?: string;
  environment?: string;
  asc_issuer_id?: string;
  asc_key_id?: string;
  has_private_key: boolean;
  is_configured: boolean;
  notification_url?: string;
}

export interface UpdateAppleIapCredentialsRequest {
  bundle_id?: string;
  environment?: string;
  asc_issuer_id?: string;
  asc_key_id?: string;
  // Send the full .p8 PEM to set/replace; omit to leave the stored key untouched; "" to clear.
  asc_private_key_pem?: string;
}

// Deprecated aliases retained for any callers still importing the old names.
export type SubscriptionTemplate = SubscriptionPlan;
export type CreateSubscriptionTemplateRequest = CreateSubscriptionPlanRequest;
export type UpdateSubscriptionTemplateRequest = UpdateSubscriptionPlanRequest;

export const PAYMENT_STATUSES = [
  { value: "Completed", label: "Completed" },
  { value: "Pending", label: "Pending" },
  { value: "Processing", label: "Processing" },
  { value: "Failed", label: "Failed" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Refunded", label: "Refunded" },
  { value: "PartiallyRefunded", label: "Partially Refunded" },
] as const;

export const PAYMENT_TYPES = [
  { value: "web", label: "Web" },
  { value: "mobile", label: "Mobile" },
] as const;

export const CURRENCIES = [
  { value: "usd", label: "USD" },
  { value: "eur", label: "EUR" },
  { value: "gbp", label: "GBP" },
  { value: "cad", label: "CAD" },
  { value: "aud", label: "AUD" },
] as const;

export const SUBSCRIPTION_STATUSES = [
  { value: "incomplete", label: "Incomplete" },
  { value: "incomplete_expired", label: "Incomplete Expired" },
  { value: "trialing", label: "Trialing" },
  { value: "active", label: "Active" },
  { value: "past_due", label: "Past Due" },
  { value: "canceled", label: "Canceled" },
  { value: "unpaid", label: "Unpaid" },
] as const;

export const BILLING_INTERVALS = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
  { value: "year", label: "Annually" },
] as const;

export interface Offer {
  id: string;
  name: string;
  description: string | null;
  kind: "discount" | "trial_extension" | "referral";
  redeemer_reward_json: string;
  referrer_reward_json: string | null;
  eligibility_json: string | null;
  valid_from: string | null;
  valid_until: string | null;
  total_redemption_cap: number | null;
  per_user_redemption_cap: number;
  status: "active" | "paused" | "expired";
  created_at: string;
  updated_at: string;
  primary_code?: OfferCode | null;
}

// Mirror of EligibilityRules in Anythink.Domain.Payments.Models. Stored as a JSON string on the
// Offer row; null/empty = open to everyone.
export type EligibilityRuleType =
  | "subscribed_to_plan"
  | "subscription_min_tier"
  | "redeemed_offer";

export interface EligibilityRule {
  type: EligibilityRuleType;
  plan_ids?: number[];
  tier_rank?: number;
  offer_id?: string;
}

export interface EligibilityRules {
  match: "all" | "any";
  rules: EligibilityRule[];
}

export interface UserRef {
  id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
}

export interface OfferRedemption {
  id: string;
  offer_id: string;
  code_id: string;
  redeemer_user_id: number;
  redeemer_subscription_id: string | null;
  redeemer_reward_status: string;
  referrer_user_id: number | null;
  referrer_reward_status: string | null;
  redeemed_at: string;
  applied_at: string | null;
  reversed_at: string | null;
  redeemer?: UserRef | null;
  referrer?: UserRef | null;
  code_slug?: string | null;
  offer_name?: string | null;
}

export interface PersonalCode {
  slug: string;
  offer_id: string;
  redemption_count: number;
  offer_name?: string | null;
}

export interface OwnedPersonalCode {
  owner_user_id: number;
  slug: string;
  offer_id: string;
  redemption_count: number;
  offer_name?: string | null;
}

export interface UserBalance {
  unit_type: "points" | "credit" | string;
  currency?: string | null;
  total: number;
}

export interface UserBalanceLedgerEntry {
  id: string;
  user_id: number;
  unit_type: "points" | "credit" | string;
  currency?: string | null;
  delta: number;
  source: string;
  source_ref?: string | null;
  reason?: string | null;
  created_by_user_id?: number | null;
  created_at: string;
  holder?: UserRef | null;
  created_by?: UserRef | null;
}

export interface TenantBalanceHolder {
  user_id: number;
  unit_type: "points" | "credit" | string;
  currency?: string | null;
  total: number;
  user?: UserRef | null;
}

export interface AdjustUserBalanceRequest {
  unit_type: "points" | "credit";
  currency?: string | null;
  delta: number;
  reason?: string | null;
}

export interface UserRedeemedCode {
  redeemer_user_id: number;
  redemption_id: string;
  code_id: string;
  slug: string;
  offer_id: string;
  offer_name?: string | null;
  redeemer_reward_status: string;
  redeemed_at: string;
}

export interface OfferCode {
  id: string;
  offer_id: string;
  slug: string;
  owner_user_id: number | null;
  redemption_count: number;
  created_at: string;
  owner?: UserRef | null;
}

// Reward type → label + supported-in-v1 flag for the create/edit form picker.
export const REWARD_TYPES = [
  { value: "none", label: "None", enabled: true, description: "No reward" },
  { value: "trial_extension", label: "Extended free trial", enabled: true, description: "Add days to the user's engagement trial" },
  { value: "subscription_extension", label: "Subscription extension", enabled: true, description: "Push the user's renewal date forward (Stripe or Apple)" },
  { value: "points", label: "Points", enabled: true, description: "Loyalty points credited to the user's balance" },
  { value: "credit", label: "Account credit", enabled: true, description: "Currency credit on the user's balance" },
  { value: "discount", label: "Discount", enabled: false, description: "Phase 2 — Stripe coupon sync" },
  { value: "cash_payout", label: "Cash back", enabled: false, description: "Phase 2 — applied as customer balance credit on next invoice" },
  { value: "tiered", label: "Tiered", enabled: false, description: "Phase 2 — reward escalation by referral count" },
] as const;

// Sentinel used to encode "unlimited" in PromoCodes per-user cap. The backend column is a
// non-nullable int so we cap at int.MaxValue and treat that as "unlimited" on read+write.
export const UNLIMITED_PER_USER_CAP = 2147483647;

export const OFFER_STATUSES = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "expired", label: "Expired" },
] as const;

export interface SubscriptionUser {
  id: number;
  tenant_id: number;
  subscription_id: string;
  user_id: number;
  readonly: boolean;
  user: User;
}

export interface UpdateSubscriptionUserRequest {
  user_id: number;
  readonly: boolean;
}

// Utility functions for subscriptions

/**
 * Format currency amount
 */
export function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);
}

/**
 * Format date to locale string
 */
export function formatDate(date: string | Date): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format date as relative time (e.g., "in 5 days", "2 days ago")
 */
export function formatRelativeDate(date: string | Date): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = dateObj.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  if (diffDays === -1) return "yesterday";
  if (diffDays > 0) return `in ${diffDays} days`;
  return `${Math.abs(diffDays)} days ago`;
}

/**
 * Calculate annual amount from recurring subscription
 */
export function calculateAnnualAmount(
  amount: number,
  interval: string,
  intervalCount: number,
): number {
  const periodsPerYear = getPeriodsPerYear(interval, intervalCount);
  return amount * periodsPerYear;
}

/**
 * Get number of periods per year for a billing interval
 */
function getPeriodsPerYear(interval: string, intervalCount: number): number {
  switch (interval) {
    case "day":
      return 365 / intervalCount;
    case "week":
      return 52 / intervalCount;
    case "month":
      return 12 / intervalCount;
    case "year":
      return 1 / intervalCount;
    default:
      return 1;
  }
}

/**
 * Get next billing date for a subscription
 */
export function getNextBillingDate(subscription: Subscription): Date | null {
  if (subscription.current_period_ends_at) {
    return new Date(subscription.current_period_ends_at);
  }
  return null;
}

/**
 * Check if subscription is in a critical state
 */
export function isSubscriptionCritical(subscription: Subscription): boolean {
  return (
    subscription.status === "past_due" ||
    subscription.status === "unpaid" ||
    subscription.status === "incomplete_expired"
  );
}

/**
 * Check if subscription is active or trialing
 */
export function isSubscriptionActive(subscription: Subscription): boolean {
  return subscription.status === "active" || subscription.status === "trialing";
}

/**
 * Check if subscription is scheduled to cancel
 */
export function isSubscriptionScheduledCancel(
  subscription: Subscription,
): boolean {
  return !!subscription.auto_cancel_at;
}

// Single source of truth for translating provider keys to display labels + Radix accent colours.
// Used by the Payments grid, Subscriptions grid, ViewSubscription detail page, and the filter
// dropdowns. Legacy rows with no provider are treated as Stripe (pre-provider-column behaviour).
export function providerLabel(provider: string | null | undefined): string {
  const key = (provider ?? "stripe").toLowerCase();
  switch (key) {
    case "apple":
      return "Apple";
    case "google":
      return "Google Play";
    case "stripe":
      return "Stripe";
    default:
      return provider ?? "—";
  }
}

export function providerBadgeColor(
  provider: string | null | undefined,
): "gray" | "indigo" | "green" {
  const key = (provider ?? "stripe").toLowerCase();
  switch (key) {
    case "apple":
      return "gray";
    case "google":
      return "green";
    default:
      return "indigo";
  }
}

/**
 * Get status color for subscription
 */
export function getSubscriptionStatusColor(
  status: string,
): "green" | "blue" | "orange" | "red" | "gray" {
  const normalizedStatus = status.toLowerCase();
  switch (normalizedStatus) {
    case "active":
      return "green";
    case "trialing":
      return "blue";
    case "past_due":
      return "orange";
    case "canceled":
    case "cancelled":
    case "unpaid":
      return "red";
    case "incomplete":
    case "incomplete_expired":
      return "gray";
    default:
      return "gray";
  }
}
